/* eslint-disable react-hooks/exhaustive-deps */
import { useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { useContext, useEffect, useState, useRef } from "react";
import { AddNodeSidePanel } from "../Graph/Panels/AddNodeSidePanel";
import {
  dataSliceService,
  sourceMetadataService,
  suggestionService,
  tagService,
  classificationService,
} from "../../../../../../services/api";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { WorkflowType } from "../../../../../../models/WorkflowModel";
import { processData } from "../GraphUtils";
import { processTagTagData } from "../Graph/TagTagUtils";
import { FiX } from "react-icons/fi";
import CreateDataSlice from "../../../../../utils/CreateDataSlice";
import { ExtractBanner } from "./components/ExtractBanner";
import {
  getLayoutedElements,
  getNewConditionsFromNodes,
} from "./components/Utils";
import { ExtractMetaGraphPage } from "./components/ExtractMetaGraphPage";
import { DB_OPTIONS } from "../../../../../../components/MainContent/DeasyConfig/ConfigElements/utils";
import React from "react";
import { toast } from "react-hot-toast";
import BulkImproveTagsModal from "../../../../SavedTags/components/TagTables/BulkImproveTags";
import BulkStandardizeTagsModal from "../../../../SavedTags/components/TagTables/BulkStandardizeTagsModal";
import { Modal } from "@mui/material";
import { FiSearch, FiPlus, FiMinus, FiInfo } from "react-icons/fi";
import { FilePickerModal } from "../../../../../utils/FilePickerModal";
import { v4 as uuidv4 } from "uuid";
import {
  cleanActiveValuesFromGraph,
  hasCorruptActiveValues,
} from "../Graph/CleanActiveValuesUtil";
import { getActiveValues } from "../Graph/ValueActivationModal";

// Helper function to process graph data based on view mode
// This ensures that tag-tag mode is maintained when adding/removing tags
// instead of reverting to tag-value mode
const processGraphDataByMode = (
  data,
  stats,
  showNodes,
  layout,
  viewMode,
  selectedDiscoveryGraph = null,
) => {
  if (viewMode === "tag-value") {
    return processData(data, stats, showNodes, layout);
  } else {
    return processTagTagData(data, stats, layout, selectedDiscoveryGraph);
  }
};

const ExtractMetadataTab = () => {
  const {
    deasyUserConfig,
    setCurrentScreen,
    setSelectedDataSlice,
    vdbFilesCount,
    isConfigRefreshing,
    setLoadingVdbFilesCount,
    setVdbFilesCount,
    loadingVdbFilesCount,
    vdbLastActiveProfileName,
    savedTags,
    setSavedTags,
    selectedDataSlice,
  } = useContext(BaseContext);

  const {
    setGraphs,
    hierarchyStats,
    discoveryGraphData,
    updateDiscoveryGraphData,
    selectedDiscoveryGraph,
    selectedNodes,
    sidePanelOpen,
    shouldFitView,
    setShouldFitView,
    handleSaveGraph,
    loadingUniqueTags,
    fetchUniqueTags,
    fetchGraphs,
    graphs,
    loadingGraphs,
    showNodesWithNoValue: graphShowNodesWithNoValue,
    graphLayoutDirection,
    graphViewMode,
  } = useContext(GraphContext);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [generatingSchema, setGeneratingSchema] = useState(false);
  const [selectedNodesFileCount, setSelectedNodesFileCount] = useState(0);
  const [isLoadingFileCount, setIsLoadingFileCount] = useState(false);
  const [isManuallyUpdatingNodes, setIsManuallyUpdatingNodes] = useState(false);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);
  const [hasInitiallySetView, setHasInitiallySetView] = useState(false);

  const [selectedClassifyFiles, setSelectedClassifyFiles] = useState([]);

  const [excludedTags, setExcludedTags] = useState(new Set());

  // Extract metadata modal states
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [showFilePickerModal, setShowFilePickerModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Safe destructuring with default values to prevent "Cannot read properties of undefined" errors
  const vdbmConfigs = deasyUserConfig?.vdbmConfig?.Configs || {};
  const vdbmLastActive = deasyUserConfig?.vdbmConfig?.LastActive || "";
  const llmLastActive = deasyUserConfig?.llmConfig?.LastActive || "";
  const deasyApiKey = deasyUserConfig?.deasyApiKey || "";
  const vectorDBConfiguration = vdbmConfigs?.[vdbLastActiveProfileName];
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const hasInitiallyRendered = useRef(false);

  // Bulk operation states
  const [showBulkImproveConfirm, setShowBulkImproveConfirm] = useState(false);
  const [showBulkImproveModal, setShowBulkImproveModal] = useState(false);
  const [improvingTags, setImprovingTags] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [tagsToImprove, setTagsToImprove] = useState([]);
  const [showBulkStandardizeModal, setShowBulkStandardizeModal] =
    useState(false);

  const lastVdbActiveRef = useRef(vdbLastActiveProfileName);
  const isInitialMount = useRef(true);
  const [isConnectorSwitching, setIsConnectorSwitching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Refresh graphs when connector changes
  useEffect(() => {
    const refreshDataForNewConnector = async () => {
      // Handle initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
        lastVdbActiveRef.current = vdbLastActiveProfileName;

        // Load initial data
        if (vdbLastActiveProfileName && deasyApiKey) {
          setIsInitialLoading(true);
          setIsRefreshing(true);

          try {
            // Fetch saved tags for the initial connector
            try {
              const savedTagsResponse = await tagService.getTags(
                deasyApiKey,
                vdbLastActiveProfileName,
              );
              setSavedTags(savedTagsResponse?.data?.tags || []);
            } catch (error) {
              console.error("Error fetching saved tags:", error);
            }
          } catch (error) {
            console.error("Error loading initial data:", error);
          } finally {
            setIsInitialLoading(false);
            setIsRefreshing(false);
          }
        }
        return;
      }

      if (
        lastVdbActiveRef.current !== vdbLastActiveProfileName &&
        vdbLastActiveProfileName &&
        deasyApiKey
      ) {
        // Connector has changed
        lastVdbActiveRef.current = vdbLastActiveProfileName;

        // Set loading state
        setIsConnectorSwitching(true);
        setIsRefreshing(true);

        try {
          // Don't clear nodes immediately - keep showing old data while loading

          // Reset the initially rendered flag so the graph fits view when new data loads
          hasInitiallyRendered.current = false;

          // Fetch data in parallel for better performance
          const savedTagsResponse = await tagService
            .getTags(deasyApiKey, vdbLastActiveProfileName)
            .catch((error) => {
              console.error("Error fetching saved tags:", error);
              return { data: { tags: [] } };
            });

          setSavedTags(savedTagsResponse?.data?.tags || []);

          // Clear nodes only after we have new data
          // The graph will be rebuilt when hierarchyStats updates
        } catch (error) {
          console.error("Error refreshing data for new connector:", error);
        } finally {
          setIsConnectorSwitching(false);
          setIsRefreshing(false);
        }
      }
    };

    refreshDataForNewConnector();
  }, [vdbLastActiveProfileName, deasyApiKey, setSavedTags]);

  // Ensure graphs are loaded on mount
  useEffect(() => {
    if (graphs.length === 0 && deasyApiKey && !loadingGraphs) {
      fetchGraphs();
    }
  }, [graphs.length, deasyApiKey, fetchGraphs, loadingGraphs]);

  // Handle when selected graph changes
  useEffect(() => {
    if (
      selectedDiscoveryGraph &&
      Object.keys(selectedDiscoveryGraph.graph_data || {}).length > 0
    ) {
      // Check for and clean corrupt activeValues nodes
      if (hasCorruptActiveValues(selectedDiscoveryGraph.graph_data)) {
        console.log("Cleaning corrupt activeValues nodes from graph");
        const cleanedData = cleanActiveValuesFromGraph(
          selectedDiscoveryGraph.graph_data,
        );

        // Update the graph with cleaned data
        updateDiscoveryGraphData(cleanedData);

        // Save the cleaned graph
        handleSaveGraph({
          ...selectedDiscoveryGraph,
          graph_data: cleanedData,
        });

        toast.success("Cleaned up corrupt graph data");
      }

      // Only fit view on initial graph load, not on data updates
      // This prevents unwanted view changes during data updates
    }
  }, [selectedDiscoveryGraph]);

  const handleConfirmBulkImprove = async () => {
    if (!tagsToImprove || !tagsToImprove.length) {
      console.error("No tags available in tagsToImprove array:", tagsToImprove);
      toast.error("No tags available to improve");
      return;
    }

    setShowBulkImproveConfirm(false);
    setImprovingTags(true);
    setShowBulkImproveModal(true);

    try {
      // Show loading toast
      toast.loading("Generating tag improvements...", { id: "improving-tags" });

      const improvedTagsPromises = tagsToImprove.map((tag) => {
        const isOpenEnded =
          tag.name === "<ANY>" ||
          (tag.available_values && tag.available_values.includes("<ANY>")) ||
          (tag.available_values && tag.available_values.length === 0);

        const tagContext = isOpenEnded
          ? "Tag Type: extract\n"
          : "Tag Type: classify\n";

        return suggestionService.suggestDescription(
          vdbmLastActive,
          llmLastActive,
          tag.name,
          tagContext,
          deasyApiKey,
          tag.description,
          tag.available_values || [],
          selectedDataSlice?.id || null,
        );
      });

      const results = await Promise.all(improvedTagsPromises);

      const improvedTags = results.map((result, index) => {
        try {
          if (result.data && result.data.suggestion) {
            const suggestionData = JSON.parse(result.data.suggestion);
            return {
              ...tagsToImprove[index],
              ...suggestionData,
            };
          } else {
            console.warn(
              `No suggestion data found for tag: ${tagsToImprove[index].name}`,
            );
            return tagsToImprove[index];
          }
        } catch (error) {
          console.error(
            "Error parsing suggestion for tag:",
            tagsToImprove[index].name,
            error,
          );
          return tagsToImprove[index];
        }
      });

      toast.dismiss("improving-tags");
      setSuggestedTags(improvedTags);
      toast.success(`Generated improvements for ${improvedTags.length} tags`);
    } catch (error) {
      console.error("Error improving tags:", error);
      toast.error("Failed to improve tags. Please try again.");
      setShowBulkImproveModal(false);
      toast.dismiss("improving-tags");
    } finally {
      setImprovingTags(false);
    }
  };

  const handleSaveImprovedTags = async (updatedTags, shouldComplete) => {
    if (!shouldComplete) {
      setSuggestedTags(updatedTags);
      return;
    }

    if (!deasyUserConfig || !deasyUserConfig.deasyApiKey) {
      toast.error("Authentication error: Missing API key");
      setShowBulkImproveModal(false);
      return;
    }

    const changedTags = updatedTags.filter((tag, i) => {
      const original = tagsToImprove.find((t) => t.tag_id === tag.tag_id);
      return (
        original &&
        (original.name !== tag.name || original.description !== tag.description)
      );
    });

    if (changedTags.length === 0) {
      toast.info("No tags were changed");
      setShowBulkImproveModal(false);
      return;
    }

    try {
      const updatePromises = changedTags.map((tag) => {
        const originalTag = tagsToImprove.find((t) => t.tag_id === tag.tag_id);

        const updatedTag = {
          ...originalTag,
          name: tag.name,
          description: tag.description,
        };

        return tagService
          .updateTag(updatedTag, deasyUserConfig.deasyApiKey)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              toast.error(
                "Authentication failed - please try logging out and back in",
              );
            }
            throw error;
          });
      });

      await Promise.all(updatePromises);

      const updatedSavedTags = [...savedTags];

      changedTags.forEach((changedTag) => {
        const savedTagIndex = updatedSavedTags.findIndex(
          (tag) => tag.tag_id === changedTag.tag_id,
        );
        if (savedTagIndex !== -1) {
          updatedSavedTags[savedTagIndex] = {
            ...updatedSavedTags[savedTagIndex],
            name: changedTag.name,
            description: changedTag.description,
          };
        }
      });
      setSavedTags(updatedSavedTags);

      toast.success(
        `Successfully improved ${changedTags.length} tag${changedTags.length > 1 ? "s" : ""}`,
      );
    } catch (error) {
      toast.error("Failed to save some improved tags");
    }

    setShowBulkImproveModal(false);
  };

  // Reset shouldFitView after initial render
  useEffect(() => {
    if (shouldFitView) {
      // Reset shouldFitView after a brief delay to allow the view to fit
      const timer = setTimeout(() => {
        setShouldFitView(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldFitView, setShouldFitView]);

  // Fetch the total VDB files count
  useEffect(() => {
    const fetchTotalVdbFiles = async () => {
      // Skip if already loading or we already have a count for this profile
      if (
        loadingVdbFilesCount ||
        (vdbFilesCount?.profile &&
          vdbFilesCount.profile === vdbLastActiveProfileName)
      ) {
        return;
      }

      // Validate configuration first
      const dbOption = DB_OPTIONS.find(
        (opt) => opt.value === vectorDBConfiguration?.type,
      );
      const hasRequiredFields = dbOption?.fields
        .filter((field) => !field.toLowerCase().includes("key"))
        .every((field) => vectorDBConfiguration?.[field]);

      if (!dbOption || !hasRequiredFields) {
        console.log(
          "Skipping VDB files count fetch - incomplete vector DB config",
        );
        return;
      }

      try {
        setLoadingVdbFilesCount(true);
        const response = await sourceMetadataService.getVdbTotalFiles(
          vectorDBConfiguration.name,
          deasyApiKey,
        );
        setVdbFilesCount({
          total_files: response.data.total_files,
          profile: vdbLastActiveProfileName,
        });
      } catch (error) {
        console.error("Error fetching total VDB files:", error);
        // Don't set to null on error, keep previous state
      } finally {
        setLoadingVdbFilesCount(false);
      }
    };

    if (deasyApiKey && vectorDBConfiguration) {
      console.log(
        "Fetching total VDB files, confirmed having both api and vector db config",
      );
      fetchTotalVdbFiles();
    }
  }, [
    deasyApiKey,
    vectorDBConfiguration,
    vdbLastActiveProfileName,
    vdbFilesCount,
  ]);

  // Update nodes when hierarchyStats changes
  useEffect(() => {
    const discoveryGraphDataIsEmpty =
      Object.keys(discoveryGraphData || {}).length === 0;

    // If a load/switch is in progress AND the incoming graph data is (temporarily) empty,
    // AND we currently have nodes displayed, then don't wipe them out.
    // Wait for the actual data or for the loading state to finish.
    if (
      (isInitialLoading || isConnectorSwitching || isConfigRefreshing) &&
      discoveryGraphDataIsEmpty &&
      nodes.length > 0
    ) {
      return; // Keep existing nodes, wait for actual data or loading to finish
    }

    // Skip automatic layout when manually updating nodes to preserve user's view position
    if (isManuallyUpdatingNodes) {
      return;
    }

    // Skip the very first render to prevent double processing
    if (!hasInitiallyRendered.current) {
      hasInitiallyRendered.current = true;
      // If discoveryGraphData is empty AND a load is active on this very first pass,
      // returning here defers processing of (potentially transient) empty data.
      if (
        discoveryGraphDataIsEmpty &&
        (isInitialLoading || isConnectorSwitching || isConfigRefreshing)
      ) {
        return;
      }
      // Don't auto-fit view to prevent unwanted view changes
      // Let users manually control the view
    }

    // Always process the graph data to ensure the root node is displayed
    // Even if discoveryGraphData is empty, processGraphDataByMode should return at least the root node
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphDataByMode(
      discoveryGraphData || {},
      hierarchyStats,
      graphShowNodesWithNoValue,
      graphLayoutDirection,
      graphViewMode,
      selectedDiscoveryGraph,
    );

    // 1. Layout the nodes/edges with dagre
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      updatedNodes,
      updatedEdges,
      graphLayoutDirection === "horizontal" ? "LR" : "TB",
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [
    hierarchyStats,
    graphShowNodesWithNoValue,
    discoveryGraphData,
    setNodes,
    setEdges,
    setShouldFitView,
    isInitialLoading,
    isConnectorSwitching,
    isConfigRefreshing,
    graphLayoutDirection,
    isManuallyUpdatingNodes, // Added to dependencies
  ]);

  // Function to position new nodes near their parent while preserving existing positions
  const positionNewNodesNearParent = (
    updatedNodes,
    currentNodes,
    currentEdges,
    layoutDirection,
  ) => {
    // Create a map of existing node positions
    const existingPositions = new Map();
    currentNodes.forEach((node) => {
      existingPositions.set(node.id, node.position);
    });

    const nodesWithPositions = updatedNodes.map((node) => {
      // If node already exists, preserve its position
      if (existingPositions.has(node.id)) {
        return {
          ...node,
          position: existingPositions.get(node.id),
        };
      }

      // For new nodes, find their parent and position them nearby
      const parentEdge = currentEdges?.find((edge) => edge.target === node.id);
      let position = { x: 0, y: 0 }; // Default position

      if (parentEdge) {
        const parentNode = currentNodes.find((n) => n.id === parentEdge.source);
        if (parentNode) {
          // Position new node relative to parent
          const offset =
            layoutDirection === "horizontal"
              ? { x: 300, y: Math.random() * 100 - 50 } // Horizontal layout
              : { x: Math.random() * 100 - 50, y: 200 }; // Vertical layout

          position = {
            x: parentNode.position.x + offset.x,
            y: parentNode.position.y + offset.y,
          };
        }
      } else if (node.type === "root") {
        // Keep root at center or find existing root position
        const existingRoot = currentNodes.find((n) => n.type === "root");
        position = existingRoot ? existingRoot.position : { x: 0, y: 0 };
      }

      return {
        ...node,
        position,
      };
    });

    return nodesWithPositions;
  };

  const handleAddNodeApplied = async (selectedNodeData, tag) => {
    if (!selectedNodeData || !tag) return;

    // Set flag to prevent automatic layout recalculation
    setIsManuallyUpdatingNodes(true);

    // Create a deep copy of the data structure
    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));

    const addNodeToStructure = (data, nodePath, newTag) => {
      let current = data;

      // Use the tag's available_values directly
      const tagAvailableValues = newTag.available_values || [];

      // Create the tag object with TagAvailableValues
      const tagObject = {
        TagAvailableValues: tagAvailableValues,
      };

      if (selectedNodeData.label === "Root") {
        // Add directly to root
        current[newTag.name] = { ...tagObject };

        // Add value nodes
        if (!tagAvailableValues.length) {
          current[newTag.name]["<ANY>"] = {};
        } else {
          tagAvailableValues.forEach((value) => {
            current[newTag.name][value] = {};
          });
        }
      } else if (selectedNodeData.nodeType === "value") {
        // Navigate to the correct position
        for (let i = 0; i < nodePath.length; i++) {
          const pathItem = nodePath[i];
          if (!(pathItem in current)) {
            current[pathItem] = {};
          }
          current = current[pathItem];
        }

        // Add the new tag
        current[newTag.name] = { ...tagObject };

        // Add value nodes
        if (!tagAvailableValues.length) {
          current[newTag.name]["<ANY>"] = {};
        } else {
          tagAvailableValues.forEach((value) => {
            current[newTag.name][value] = {};
          });
        }
      } else if (selectedNodeData.nodeType === "value") {
        // Adding tag under a specific value (used by value selector modal)
        for (let i = 0; i < nodePath.length; i++) {
          const pathItem = nodePath[i];
          if (!(pathItem in current)) {
            current[pathItem] = {};
          }
          current = current[pathItem];
        }

        // Add the new tag under this value
        current[newTag.name] = { ...tagObject };

        // Add value nodes to the new tag
        if (!tagAvailableValues.length) {
          current[newTag.name]["<ANY>"] = {};
        } else {
          tagAvailableValues.forEach((value) => {
            current[newTag.name][value] = {};
          });
        }
      } else {
        // For question nodes (original behavior)
        for (let i = 0; i < nodePath.length - 2; i++) {
          const pathItem = nodePath[i];
          if (!(pathItem in current)) {
            current[pathItem] = {};
          }
          current = current[pathItem];
        }

        const targetNodeName = nodePath[nodePath.length - 2];
        current[targetNodeName][newTag.name] = { ...tagObject };

        // Add value nodes
        if (!tagAvailableValues.length) {
          current[targetNodeName][newTag.name]["<ANY>"] = {};
        } else {
          tagAvailableValues.forEach((value) => {
            current[targetNodeName][newTag.name][value] = {};
          });
        }
      }
    };

    // Add the new node to the data structure
    if (Array.isArray(tag)) {
      tag.forEach((t) =>
        addNodeToStructure(updatedData, selectedNodeData.nodePath || [], t),
      );
    } else {
      addNodeToStructure(updatedData, selectedNodeData.nodePath || [], tag);
    }

    // Update the graph data
    updateDiscoveryGraphData(updatedData);

    // Immediately save the updated graph to prevent tags from disappearing
    await handleSaveGraph({
      ...selectedDiscoveryGraph,
      graph_data: updatedData,
    });

    // Update the graphs state
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );

    // Process and update nodes/edges without layout to preserve user's view position
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphDataByMode(
      updatedData,
      hierarchyStats,
      graphShowNodesWithNoValue,
      graphLayoutDirection,
      graphViewMode,
      selectedDiscoveryGraph,
    );

    // Position new nodes near their parent instead of applying full layout
    const nodesWithPositioning = positionNewNodesNearParent(
      updatedNodes,
      nodes, // current nodes with positions
      edges, // current edges with connections
      graphLayoutDirection,
    );

    setNodes(nodesWithPositioning);
    setEdges(updatedEdges);

    // Reset the manual update flag
    setIsManuallyUpdatingNodes(false);

    // Don't auto-focus when adding tags to preserve user's current view position
    // Users should stay where they are when adding new tags
  };

  // Update the useEffect for node highlighting
  useEffect(() => {
    // Get all tags that would be extracted from selected nodes
    const extractableTags = new Set();
    const excludedNodeTags = new Set();
    const selectedNodePaths = new Set(selectedNodes.map((node) => node.id));

    // Helper to collect tags from a node
    const collectTags = (data) => {
      const traverse = (obj) => {
        if (!obj || typeof obj !== "object") return;
        Object.entries(obj).forEach(([key, value]) => {
          if (key !== "TagAvailableValues" && value?.TagAvailableValues) {
            if (excludedTags.has(key)) {
              excludedNodeTags.add(key);
            } else {
              extractableTags.add(key);
            }
          }
          if (typeof value === "object" && value !== null) {
            traverse(value);
          }
        });
      };
      traverse(data);
    };

    if (!selectedNodes.length) {
      // When no nodes selected, check all nodes for tags
      collectTags(discoveryGraphData);
    } else {
      // For selected nodes, only check their subtrees
      selectedNodes.forEach((node) => {
        const path = node.data.nodePath;
        let currentData = discoveryGraphData;

        // Navigate to the selected node
        for (let i = 0; i < path.length; i++) {
          if (currentData[path[i]]) {
            currentData = currentData[path[i]];
          }
        }

        collectTags(currentData);
      });
    }

    // Update node styling to show both extractable and excluded tags
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        const hasExtractableTag = node.data?.nodePath?.some((path) =>
          extractableTags.has(path),
        );
        const hasExcludedTag = node.data?.nodePath?.some((path) =>
          excludedNodeTags.has(path),
        );
        const isSelectedNode = selectedNodePaths.has(node.id);
        const isRootNode = node.type === "root";

        let nodeClass = (node.className || "")
          .replace(/bg-primary\/\d+|opacity-\d+|bg-gray-\d+/g, "")
          .trim();
        if (hasExcludedTag) {
          nodeClass = `${nodeClass}  opacity-40`;
        }
        // Only apply opacity if it's not the root node, not selected, and not extractable/excluded
        if (
          !isRootNode &&
          !hasExtractableTag &&
          !hasExcludedTag &&
          !isSelectedNode
        ) {
          nodeClass = `${nodeClass} opacity-40`;
        }

        return {
          ...node,
          className: nodeClass.trim(),
        };
      }),
    );
  }, [selectedNodes, discoveryGraphData, excludedTags, setNodes]);

  // Fetch file count when selected nodes change
  useEffect(() => {
    const fetchFileCount = async () => {
      if (!selectedNodes.length) {
        setSelectedNodesFileCount(0);
        return;
      }

      setIsLoadingFileCount(true);
      try {
        const newConditions = getNewConditionsFromNodes(selectedNodes);
        const response = await dataSliceService.getDatasliceFileCount(
          vectorDBConfiguration.name,
          newConditions,
          selectedDataSlice?.id || null,
          deasyApiKey,
        );
        if (response.data?.file_count !== undefined) {
          setSelectedNodesFileCount(response.data.file_count);
        }
      } catch (error) {
        console.error("Error fetching file count:", error);
        setSelectedNodesFileCount(0);
      } finally {
        setIsLoadingFileCount(false);
      }
    };

    fetchFileCount();
  }, [selectedNodes, deasyApiKey, vectorDBConfiguration]);

  const hasInitiallyFetchedTags = React.useRef(false);

  useEffect(() => {
    if (
      !hasInitiallyFetchedTags.current &&
      deasyApiKey &&
      vectorDBConfiguration &&
      !loadingUniqueTags
    ) {
      hasInitiallyFetchedTags.current = true;
      fetchUniqueTags();
    }
  }, [deasyApiKey, vectorDBConfiguration, fetchUniqueTags, loadingUniqueTags]);

  // Listen for custom events from ExtractBanner for bulk operations
  useEffect(() => {
    // Create a function reference to the current version of handleConfirmBulkImprove
    const confirmBulkImprove = (tags) => {
      if (!tags || !tags.length) {
        toast.error("No tags available to improve");
        return;
      }

      setTagsToImprove(tags);
      setShowBulkImproveConfirm(false);
      setImprovingTags(true);
      setShowBulkImproveModal(true);

      const handleTagsImprovement = async () => {
        try {
          toast.loading("Generating tag improvements...", {
            id: "improving-tags",
          });

          const improvedTagsPromises = tags.map((tag) => {
            const isOpenEnded =
              tag.name === "<ANY>" ||
              (tag.available_values &&
                tag.available_values.includes("<ANY>")) ||
              (tag.available_values && tag.available_values.length === 0);

            const tagContext = isOpenEnded
              ? "Tag Type: extract\n"
              : "Tag Type: classify\n";

            return suggestionService.suggestDescription(
              vdbmLastActive,
              llmLastActive,
              tag.name,
              tagContext,
              deasyApiKey,
              tag.description,
              tag.available_values || [],
              selectedDataSlice?.id || null,
            );
          });

          const results = await Promise.all(improvedTagsPromises);

          const improvedTags = results.map((result, index) => {
            try {
              if (result.data && result.data.suggestion) {
                const suggestionData = JSON.parse(result.data.suggestion);
                return {
                  ...tags[index],
                  ...suggestionData,
                };
              } else {
                return tags[index];
              }
            } catch (error) {
              console.error(
                "Error parsing suggestion for tag:",
                tags[index].name,
                error,
              );
              return tags[index];
            }
          });

          toast.dismiss("improving-tags");
          setSuggestedTags(improvedTags);
          toast.success(
            `Generated improvements for ${improvedTags.length} tags`,
          );
        } catch (error) {
          console.error("Error improving tags:", error);
          toast.error("Failed to improve tags. Please try again.");
          setShowBulkImproveModal(false);
          toast.dismiss("improving-tags");
        } finally {
          setImprovingTags(false);
        }
      };

      handleTagsImprovement();
    };

    const handleBulkImproveConfirmed = (event) => {
      const { tags } = event.detail;
      if (tags && tags.length) {
        confirmBulkImprove(tags);
      } else {
        toast.error("Failed to start tag improvement: No tags available");
      }
    };
    // Remove any existing event listeners before adding new ones
    document.removeEventListener(
      "bulkImproveConfirmed",
      handleBulkImproveConfirmed,
    );

    // Add event listeners
    document.addEventListener(
      "bulkImproveConfirmed",
      handleBulkImproveConfirmed,
    );
    return () => {
      document.removeEventListener(
        "bulkImproveConfirmed",
        handleBulkImproveConfirmed,
      );
    };
  }, [deasyApiKey, vdbmLastActive, llmLastActive, selectedDataSlice]);

  // Handle file picker modal close
  useEffect(() => {
    // When file picker modal closes and we have made a selection, proceed to tag confirmation
    if (!showFilePickerModal && showExtractModal) {
      setShowExtractModal(false);

      // Check if user actually made a selection (not cancelled)
      // If selectedClassifyFiles is an empty array and no nodes are selected, user likely cancelled
      if (
        Array.isArray(selectedClassifyFiles) &&
        selectedClassifyFiles.length === 0 &&
        selectedNodes.length === 0
      ) {
        // User cancelled or didn't select any files
        return;
      }

      // Get tags with their paths based on selected nodes or all tags
      const availableTagsWithPaths = getTagsFromSelectedNodesWithPaths(
        selectedNodes,
        discoveryGraphData,
      );

      // Convert Map to array of tag objects
      const tagsWithPaths = Array.from(availableTagsWithPaths.values());

      if (tagsWithPaths.length === 0) {
        toast.error(
          "No tags available to extract. Please add some tags to your metadata schema first.",
        );
        return;
      }

      // Calculate file count
      const fileCount =
        selectedClassifyFiles === null
          ? vdbFilesCount?.total_files || 0
          : selectedClassifyFiles.length || selectedNodesFileCount;

      setConfirmationDetails({
        tags: tagsWithPaths.map((t) => t.name), // Keep simple array for backward compatibility
        tagsWithPaths: tagsWithPaths, // Add full tag data with paths
        fileCount: fileCount,
        isRootSelection: selectedClassifyFiles === null,
      });

      setShowConfirmDialog(true);
    }
  }, [showFilePickerModal, showExtractModal]);

  // Add function to handle extract metadata
  const handleExtractMetadata = () => {
    // Set flag to indicate we're in extract flow
    setShowExtractModal(true);
    // Open file picker modal
    setShowFilePickerModal(true);
  };

  // Get all tags from the graph with their paths
  const getAllTagsFromGraphWithPaths = (data) => {
    const tags = new Map(); // Map of tagName -> {name, paths: []}

    const traverse = (obj, currentPath = []) => {
      if (!obj || typeof obj !== "object") return;

      Object.entries(obj).forEach(([key, value]) => {
        if (key !== "TagAvailableValues" && value?.TagAvailableValues) {
          const tagPath = [...currentPath, key];

          if (!tags.has(key)) {
            tags.set(key, { name: key, paths: [] });
          }
          tags.get(key).paths.push(tagPath);
        }

        if (typeof value === "object" && value !== null) {
          const newPath = [...currentPath, key];
          traverse(value, newPath);
        }
      });
    };

    traverse(data);
    return tags;
  };

  // Get tags from selected nodes with paths
  const getTagsFromSelectedNodesWithPaths = (nodes, graphData) => {
    const tags = new Map(); // Map of tagName -> {name, paths: []}

    if (!nodes.length) {
      // If no nodes selected, get all tags
      return getAllTagsFromGraphWithPaths(graphData);
    }

    nodes.forEach((node) => {
      const path = node.data.nodePath;
      let currentData = graphData;

      // Navigate to the selected node
      for (let i = 0; i < path.length; i++) {
        if (currentData[path[i]]) {
          currentData = currentData[path[i]];
        }
      }

      // Collect tags from this subtree
      const traverse = (obj, currentPath) => {
        if (!obj || typeof obj !== "object") return;
        Object.entries(obj).forEach(([key, value]) => {
          if (key !== "TagAvailableValues" && value?.TagAvailableValues) {
            const tagPath = [...currentPath, key];

            if (!tags.has(key)) {
              tags.set(key, { name: key, paths: [] });
            }
            tags.get(key).paths.push(tagPath);
          }
          if (typeof value === "object" && value !== null) {
            traverse(value, [...currentPath, key]);
          }
        });
      };
      traverse(currentData, path);
    });

    return tags;
  };

  // Handle tag exclusion
  const handleTagExclusion = (tagName, e) => {
    e.stopPropagation();
    setExcludedTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagName)) {
        newSet.delete(tagName);
      } else {
        newSet.add(tagName);
      }
      return newSet;
    });
  };

  // Build subset of graph from included tags
  const buildSubsetOfGraphFromIncluded = (includedTags, graphData) => {
    const subset = {};

    const traverse = (source, target, path = []) => {
      if (!source || typeof source !== "object") return;

      Object.entries(source).forEach(([key, value]) => {
        if (key === "TagAvailableValues") {
          target[key] = value;
        } else if (includedTags.includes(key) && value?.TagAvailableValues) {
          // This is an included tag
          target[key] = {
            TagAvailableValues: value.TagAvailableValues,
          };
          // Continue traversing its children
          traverse(value, target[key], [...path, key]);
        } else if (typeof value === "object" && !value?.TagAvailableValues) {
          // This is a value node or other structure, check if it contains included tags
          const hasIncludedTag = Object.keys(value).some(
            (k) => includedTags.includes(k) && value[k]?.TagAvailableValues,
          );

          if (hasIncludedTag || path.some((p) => includedTags.includes(p))) {
            target[key] = {};
            traverse(value, target[key], path);
          }
        }
      });
    };

    traverse(graphData, subset);
    return subset;
  };

  const removePendingJob = (jobId) => {
    const existingPendingJobs = JSON.parse(
      localStorage.getItem("pendingProgressJobs") || "[]",
    );
    const updatedPendingJobs = existingPendingJobs.filter(
      (job) => job.tracker_id !== jobId,
    );
    localStorage.setItem(
      "pendingProgressJobs",
      JSON.stringify(updatedPendingJobs),
    );
  };

  // Handle confirmed extraction
  const handleConfirmedExtract = async () => {
    if (!confirmationDetails) return;

    setIsConfirmLoading(true);

    // Filter out excluded tags for processing
    const selectedTags = confirmationDetails.tags.filter(
      (tag) => !excludedTags.has(tag),
    );

    // If no tags are selected, don't proceed
    if (selectedTags.length === 0) {
      setIsConfirmLoading(false);
      toast.error("No tags selected for extraction");
      return;
    }

    // Build the graph subset based on included tags
    const graphSubset = buildSubsetOfGraphFromIncluded(
      selectedTags,
      discoveryGraphData,
    );

    const jobID = uuidv4();
    const pendingJob = {
      tracker_id: jobID,
      task_type: "classify",
      status: "pending",
      start_time: new Date().toISOString(),
      progress: 0,
      total_files: confirmationDetails.fileCount,
      processed_files: 0,
      file_details: {
        total: confirmationDetails.fileCount,
        processed: 0,
        successful: 0,
        failed: 0,
      },
      additional_stats: {
        tags_created: 0,
        tags_selected: selectedTags.length,
      },
      is_pending: true,
    };

    const existingPendingJobs = JSON.parse(
      localStorage.getItem("pendingProgressJobs") || "[]",
    );
    const updatedPendingJobs = [...existingPendingJobs, pendingJob];
    localStorage.setItem(
      "pendingProgressJobs",
      JSON.stringify(updatedPendingJobs),
    );

    // save the graph
    await handleSaveGraph();

    try {
      if (selectedClassifyFiles === null || selectedNodes.length > 0) {
        const conditions =
          selectedNodes.length > 0
            ? getNewConditionsFromNodes(selectedNodes)
            : null;

        classificationService.classifyAll(
          vdbLastActiveProfileName,
          llmLastActive,
          null,
          deasyApiKey,
          jobID,
          selectedDataSlice?.id || null,
          vdbFilesCount?.total_files || null,
          graphSubset,
          conditions,
          overwriteExisting,
          selectedTags,
          selectedDiscoveryGraph?.graph_name,
        );
      } else if (selectedClassifyFiles.length > 0) {
        classificationService.classify(
          vdbLastActiveProfileName,
          llmLastActive,
          selectedClassifyFiles,
          null,
          deasyApiKey,
          false,
          overwriteExisting,
          graphSubset,
          jobID,
          false,
          selectedTags,
          selectedDiscoveryGraph?.graph_name,
        );
      }

      // Trigger the MiniProgressTracker grace period to look for the new job
      if (window.startTrackerGracePeriod) {
        window.startTrackerGracePeriod(5 * 60000); // 5 minute grace period
      }

      // Show success toast
      toast.success(
        "Metadata extraction started! Track progress in the bottom-left or navigate to the Progress Tracker page.",
        { duration: 6000 },
      );

      // Close dialog and reset state immediately
      setShowConfirmDialog(false);
      setTagSearchTerm("");
      setExcludedTags(new Set());
    } catch (error) {
      console.error("Error starting extraction:", error);
      removePendingJob(jobID);

      // Provide more specific error messages
      if (error.response?.status === 422) {
        toast.error(
          "Invalid request data. Please check your selection and try again.",
        );
      } else if (error.response?.data?.message) {
        toast.error(
          `Failed to start extraction: ${error.response.data.message}`,
        );
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Failed to start metadata extraction");
      }

      setShowConfirmDialog(false);
      setTagSearchTerm("");
    } finally {
      // Always reset loading state
      setIsConfirmLoading(false);
    }
  };

  // Add function to check if extract is enabled
  const canExtract = () => {
    // Extract is always enabled now since we'll handle file selection in the modal
    return true;
  };

  const getExtractDisabledReason = () => {
    return null;
  };

  // Handle Create Data Slice
  const handleCreateDataSlice = () => {
    setShowCreateDataSlice(true);
  };

  // Set view on initial load
  useEffect(() => {
    if (!hasInitiallySetView && nodes.length > 0 && !isInitialLoading) {
      setShouldFitView(true);
      setHasInitiallySetView(true);
    }
  }, [nodes.length, isInitialLoading, hasInitiallySetView, setShouldFitView]);

  // Reset hasInitiallySetView when component unmounts
  useEffect(() => {
    return () => {
      setHasInitiallySetView(false);
    };
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col h-full">
        <ExtractBanner
          // State values
          isRefreshing={isRefreshing}
          showTagNodes={true}
          showValueNodes={true}
          // Core functions
          setIsRefreshing={setIsRefreshing}
          setShouldFitView={setShouldFitView}
          // New props for data slice and extract
          onExtractMetadata={handleExtractMetadata}
          selectedNodesFileCount={selectedNodesFileCount}
          isLoadingFileCount={isLoadingFileCount}
          canExtract={canExtract()}
          extractDisabledReason={getExtractDisabledReason()}
          onCreateDataSlice={handleCreateDataSlice}
        />
        {!deasyApiKey || !vdbLastActiveProfileName || !llmLastActive ? (
          <div className="flex-1 flex items-center justify-center bg-gray-50 border-dashed border-2 border-gray-200 rounded-lg m-4">
            <div className="text-center p-8 max-w-md">
              <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg mb-4 inline-flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span className="font-medium">Missing Configuration</span>
              </div>
              <p className="text-gray-600 mb-3">
                To use the Extract Metadata feature, you need to set up:
              </p>
              <ul className="list-disc text-left pl-8 mb-4 space-y-1">
                {!deasyApiKey && (
                  <li className="text-gray-600">
                    API Key (Configuration → API Keys & Tier)
                  </li>
                )}
                {!vdbLastActiveProfileName && (
                  <li className="text-gray-600">
                    Data Source Profile (Configuration → Data Connectors)
                  </li>
                )}
                {!llmLastActive && (
                  <li className="text-gray-600">
                    LLM Profile (Configuration → Data Connectors)
                  </li>
                )}
              </ul>
            </div>
          </div>
        ) : deasyApiKey &&
          (isInitialLoading ||
            isConnectorSwitching ||
            isConfigRefreshing ||
            (nodes.length === 0 && isRefreshing && !isInitialLoading)) &&
          !sidePanelOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30" />

            {/* Modal */}
            <div className="relative bg-[#1a1f2b] rounded-xl shadow-xl p-6 m-4">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <img
                    src="/deasy_shrimp.png"
                    alt="Deasy Labs"
                    className="h-12 w-auto animate-pulse"
                  />
                </div>
                <p className="text-gray-50">
                  {isConnectorSwitching
                    ? "Switching data connector..."
                    : isConfigRefreshing
                      ? "Updating configuration..."
                      : "Loading schema..."}
                </p>
              </div>
            </div>
          </div>
        ) : (
          deasyApiKey &&
          vdbLastActiveProfileName &&
          llmLastActive && (
            <ExtractMetaGraphPage
              // Graph data
              nodes={nodes}
              edges={edges}
              // Graph state setters
              setNodes={setNodes}
              setEdges={setEdges}
              // Graph event handlers
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              handleAddNodeApplied={handleAddNodeApplied}
              // Selection state
              setSelectedClassifyFiles={setSelectedClassifyFiles}
              shouldFitView={shouldFitView}
            />
          )
        )}

        {sidePanelOpen && (
          <AddNodeSidePanel
            handleAddNodeApplied={handleAddNodeApplied}
            generatingSchema={generatingSchema}
            setGeneratingSchema={setGeneratingSchema}
          />
        )}

        {showCreateDataSlice && (
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => setShowCreateDataSlice(false)}
            fileCount={
              selectedNodes.length === 0
                ? vdbFilesCount?.total_files || 0
                : selectedNodesFileCount
            }
            discoveryGraphData={discoveryGraphData}
            selectedDiscoveryGraph={selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              setSelectedDataSlice(dataSlice);
              setCurrentScreen(WorkflowType.DATA_SLICE_MANAGEMENT);
            }}
            isGraphMode={true}
            isAllFiles={selectedNodes.length === 0}
            conditions={getNewConditionsFromNodes(selectedNodes)}
          />
        )}

        {/* Bulk Improve Tags Confirmation Modal */}
        {showBulkImproveConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
            <div
              className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-lg transform transition-all mx-4"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-primary/5 px-6 py-4 border-b border-primary/10 flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-800">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <span className="text-lg font-medium">
                    Confirm Bulk Tag Improvement
                  </span>
                </div>
                <button
                  onClick={() => setShowBulkImproveConfirm(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <FiX size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4 text-left">
                <p className="text-gray-700">
                  This will improve the descriptions of{" "}
                  <span className="font-semibold">{tagsToImprove.length}</span>{" "}
                  tags in this graph. The process may take a moment to complete.
                </p>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowBulkImproveConfirm(false)}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-5 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm transition duration-150 mr-4"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmBulkImprove}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-primary text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm transition duration-150"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Bulk Improve Tags Modal */}
        {showBulkImproveModal && (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <BulkImproveTagsModal
              isOpen={true}
              onClose={() => {
                setShowBulkImproveModal(false);
              }}
              originalTags={tagsToImprove}
              suggestedTags={suggestedTags}
              onConfirm={(tags, complete) => {
                handleSaveImprovedTags(tags, complete);
              }}
              isLoading={improvingTags}
            />
          </div>
        )}

        {/* Bulk Standardize Tags Modal */}
        {showBulkStandardizeModal && (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <BulkStandardizeTagsModal
              isOpen={true}
              onClose={() => {
                setShowBulkStandardizeModal(false);
              }}
              selectedTags={tagsToImprove}
              deasyApiKey={deasyApiKey}
              vdbProfileName={vdbLastActiveProfileName}
              llmProfileName={llmLastActive}
            />
          </div>
        )}

        {/* File Picker Modal for Extract Metadata */}
        <FilePickerModal
          openFilePicker={showFilePickerModal}
          setOpenFilePicker={(isOpen) => {
            setShowFilePickerModal(isOpen);
            // If closing the modal and we're in extract flow, reset the extract flag
            if (!isOpen && showExtractModal) {
              // Check if it was a cancel (no files selected)
              if (
                Array.isArray(selectedClassifyFiles) &&
                selectedClassifyFiles.length === 0 &&
                selectedNodes.length === 0
              ) {
                setShowExtractModal(false);
              }
            }
          }}
          selectedFiles={
            Array.isArray(selectedClassifyFiles) ? selectedClassifyFiles : []
          }
          setSelectedFiles={(files) => {
            setSelectedClassifyFiles(files);
          }}
          selectAllForEntireDB={true}
        />

        {/* Extract Metadata Confirmation Modal */}
        <Modal
          open={showConfirmDialog}
          onClose={() => {
            setShowConfirmDialog(false);
            setTagSearchTerm("");
          }}
          aria-labelledby="confirm-extract-title"
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl p-6 w-[600px] max-h-[80vh] overflow-y-auto">
            <h2
              className="text-xl font-semibold mb-4"
              id="confirm-extract-title"
            >
              Confirm Metadata Extraction
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-4">
                <p className="text-gray-600">
                  Processing {confirmationDetails?.fileCount.toLocaleString()}{" "}
                  file{confirmationDetails?.fileCount === 1 ? "" : "s"}
                  {confirmationDetails?.isRootSelection
                    ? " from the entire dataset"
                    : ""}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {
                      confirmationDetails?.tags.filter(
                        (tag) => !excludedTags.has(tag),
                      ).length
                    }{" "}
                    of {confirmationDetails?.tags.length} tags selected
                  </span>
                  <button
                    onClick={() => {
                      // Toggle all tags
                      const allTags = confirmationDetails?.tags || [];
                      // If search is active, only toggle visible tags
                      const tagsToToggle = tagSearchTerm
                        ? allTags.filter((tag) =>
                            tag
                              .toLowerCase()
                              .includes(tagSearchTerm.toLowerCase()),
                          )
                        : allTags;

                      setExcludedTags((prev) => {
                        const newSet = new Set(prev);
                        // If all visible tags are currently excluded, include all visible tags
                        if (tagsToToggle.every((tag) => newSet.has(tag))) {
                          tagsToToggle.forEach((tag) => newSet.delete(tag));
                        } else {
                          // Otherwise, exclude all visible tags
                          tagsToToggle.forEach((tag) => newSet.add(tag));
                        }
                        return newSet;
                      });
                    }}
                    className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-primary transition-colors"
                    title={
                      tagSearchTerm ? "Toggle visible tags" : "Toggle all tags"
                    }
                  >
                    {(
                      tagSearchTerm
                        ? confirmationDetails?.tags
                            .filter((tag) =>
                              tag
                                .toLowerCase()
                                .includes(tagSearchTerm.toLowerCase()),
                            )
                            .every((tag) => excludedTags.has(tag))
                        : confirmationDetails?.tags.every((tag) =>
                            excludedTags.has(tag),
                          )
                    ) ? (
                      <FiPlus size={16} />
                    ) : (
                      <FiMinus size={16} />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">
                    Selected Tags for Extraction
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search tags..."
                      className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 pl-8 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      value={tagSearchTerm}
                      onChange={(e) => setTagSearchTerm(e.target.value)}
                    />
                    <FiSearch
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                      size={14}
                    />
                    {tagSearchTerm && (
                      <button
                        onClick={() => setTagSearchTerm("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {tagSearchTerm && confirmationDetails?.tags && (
                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>
                      Showing{" "}
                      {
                        confirmationDetails.tags.filter((tag) =>
                          tag
                            .toLowerCase()
                            .includes(tagSearchTerm.toLowerCase()),
                        ).length
                      }{" "}
                      of {confirmationDetails.tags.length} tags
                    </span>
                    <span>
                      {
                        confirmationDetails.tags.filter(
                          (tag) =>
                            !excludedTags.has(tag) &&
                            tag
                              .toLowerCase()
                              .includes(tagSearchTerm.toLowerCase()),
                        ).length
                      }{" "}
                      selected in current view
                    </span>
                  </div>
                )}
                <div className="max-h-[40vh] overflow-y-auto space-y-2 bg-gray-50 rounded-lg p-4">
                  {confirmationDetails?.tags
                    .filter(
                      (tagName) =>
                        !tagSearchTerm ||
                        tagName
                          .toLowerCase()
                          .includes(tagSearchTerm.toLowerCase()),
                    )
                    .map((tagName) => {
                      const tagData = savedTags.find((t) => t.name === tagName);
                      // Find the tag info with paths
                      const tagInfo = confirmationDetails?.tagsWithPaths?.find(
                        (t) => t.name === tagName,
                      );

                      // Calculate active values for this tag
                      let activeValueCount = 0;
                      let activeValues = [];
                      let isOpenEnded = false;

                      if (
                        tagInfo &&
                        tagInfo.paths.length > 0 &&
                        discoveryGraphData
                      ) {
                        // Use the first path (they should all have the same active values)
                        activeValues = getActiveValues(
                          discoveryGraphData,
                          tagInfo.paths[0],
                        );
                        activeValueCount = activeValues.length;

                        // Check if it's open-ended
                        const totalValues =
                          tagData?.available_values?.filter(
                            (v) => v !== "<ANY>",
                          ).length || 0;
                        isOpenEnded =
                          tagData?.available_values?.includes("<ANY>") ||
                          (totalValues > 0 && activeValueCount === 0);
                      }

                      return (
                        <div key={tagName} className="flex flex-col">
                          <div
                            className={`
                            flex items-center justify-between
                            px-4 py-3 bg-white rounded-lg
                            hover:bg-gray-50 transition-all duration-200
                            ${excludedTags.has(tagName) ? "opacity-40" : ""}
                          `}
                          >
                            <div className="flex flex-col gap-1 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700 font-medium">
                                  {tagName}
                                </span>
                                {isOpenEnded ? (
                                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
                                    Open-ended
                                  </span>
                                ) : (
                                  tagData?.available_values?.length > 0 && (
                                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                                      {activeValueCount} active
                                      {activeValueCount !== 1
                                        ? " values"
                                        : " value"}
                                    </span>
                                  )
                                )}
                              </div>

                              {/* Show active values preview */}
                              {!isOpenEnded && activeValues.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {activeValues
                                    .slice(0, 3)
                                    .map((value, idx) => (
                                      <span
                                        key={idx}
                                        className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                                      >
                                        {value}
                                      </span>
                                    ))}
                                  {activeValues.length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{activeValues.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            <button
                              onClick={(e) => handleTagExclusion(tagName, e)}
                              className={`p-1 hover:bg-gray-100 rounded-full ml-2 ${
                                excludedTags.has(tagName)
                                  ? "text-red-500 bg-red-50"
                                  : "text-gray-400 hover:text-red-500"
                              }`}
                              title={
                                excludedTags.has(tagName)
                                  ? "Include tag"
                                  : "Exclude tag"
                              }
                            >
                              {excludedTags.has(tagName) ? (
                                <FiPlus size={16} />
                              ) : (
                                <FiMinus size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  {confirmationDetails?.tags &&
                    tagSearchTerm &&
                    !confirmationDetails.tags.some((tag) =>
                      tag.toLowerCase().includes(tagSearchTerm.toLowerCase()),
                    ) && (
                      <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                        <p>No tags match your search</p>
                      </div>
                    )}
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-4">
                This process may take some time depending on the size of your
                dataset.
              </p>

              {/* Overwrite toggle */}
              <div className="flex items-center justify-between mt-4 bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Overwrite existing values
                  </span>
                  <div className="relative group">
                    <FiInfo size={14} className="text-gray-400" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      If enabled, this will overwrite any existing tag values
                      for the selected files. Otherwise, only new tag values
                      will be added.
                    </div>
                  </div>
                </div>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    name="overwrite"
                    id="overwrite"
                    checked={overwriteExisting}
                    onChange={() => setOverwriteExisting(!overwriteExisting)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="overwrite"
                    className={`block h-6 overflow-hidden rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                      ${overwriteExisting ? "bg-primary" : "bg-gray-300"}`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out
                      ${overwriteExisting ? "translate-x-4" : "translate-x-0"}`}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setTagSearchTerm("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleConfirmedExtract();
                }}
                className="px-4 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-emerald-700 flex items-center gap-2"
                disabled={
                  confirmationDetails?.tags.every((tag) =>
                    excludedTags.has(tag),
                  ) || isConfirmLoading
                }
              >
                {isConfirmLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : confirmationDetails?.tags.every((tag) =>
                    excludedTags.has(tag),
                  ) ? (
                  "No Tags Selected"
                ) : (
                  `Extract Metadata (${confirmationDetails?.tags.filter((tag) => !excludedTags.has(tag)).length} tags)`
                )}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export { ExtractMetadataTab };
